import type { Asset, AssetQuery, LibraryKey, Paged, Tag, TagGroupDef } from '../types'
import { MOCK_ASSETS } from '../data/mockAssets'
import { MOCK_TAGS, MOCK_TAG_GROUPS, NON_PACKAGING_SLUG } from '../data/mockTags'
import { LOCAL_ASSETS, LOCAL_TAGS } from './localLibrary'
import { SupabaseAssetRepository } from './supabaseAssetRepository'
import { isSupabaseConfigured } from './supabaseClient'

/**
 * Data access layer (repository)
 * -------------------------------------------------------------
 * The UI only ever talks to this interface, never to a data source
 * directly. Today it is backed by MockAssetRepository. Later, a
 * SupabaseAssetRepository implements the same interface and is swapped in
 * inside getAssetRepository() — no UI code has to change.
 */
export interface AssetRepository {
  search(query: AssetQuery): Promise<Paged<Asset>>
  getById(id: string): Promise<Asset | null>
  getByIds(ids: string[]): Promise<Asset[]>
  /** One library's tags. Audit when it does not say. */
  listTags(library?: LibraryKey): Promise<Tag[]>
  /** One library's categories, in the order they should be shown. */
  listTagGroups(library?: LibraryKey): Promise<TagGroupDef[]>
}

/**
 * Override which repository the whole app uses.
 *
 * Called once, at an entry point, before anything renders. The Miro panel sets
 * the Worker-backed one: it has no Supabase session, so it cannot read the
 * library directly and must go through the backend.
 *
 * A setter rather than a parameter threaded through every hook, because the
 * choice belongs to the entry point — which surface this is — and not to any
 * individual screen. It must run before the first read; calling it later would
 * leave whatever already loaded coming from the other source.
 */
export function setAssetRepository(next: AssetRepository): void {
  repository = next
}

/** A little fake latency so loading states are visible during development */
function delay<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms))
}

function matchesText(asset: Asset, text: string, tagNameById: Map<string, string>): boolean {
  const haystack = [
    asset.title,
    asset.aiDescription ?? '',
    asset.sourceFrame ? (tagNameById.get(asset.sourceFrame) ?? asset.sourceFrame) : '',
    ...asset.tagIds.map((id) => tagNameById.get(id) ?? id),
  ]
    .join(' ')
    .toLowerCase()

  // Multiple words: every word has to match somewhere
  return text
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word))
}

export class MockAssetRepository implements AssetRepository {
  private readonly assets: Asset[]
  private readonly tags: Tag[]
  private readonly tagNameById: Map<string, string>

  /**
   * The library is whatever the user dropped into library/. The generated demo
   * set only fills in while that folder is empty, so a fresh checkout does not
   * look broken — as soon as there are real images, the placeholders step aside.
   */
  constructor(
    assets: Asset[] = LOCAL_ASSETS.length > 0 ? LOCAL_ASSETS : MOCK_ASSETS,
    tags: Tag[] = [...MOCK_TAGS, ...LOCAL_TAGS],
  ) {
    this.assets = assets
    this.tags = tags
    this.tagNameById = new Map(tags.map((t) => [t.id, t.name]))
  }

  async search(query: AssetQuery): Promise<Paged<Asset>> {
    const { text, tagGroups, packaging = 'all', source, limit = 60, after = null } = query

    let result = this.assets

    if (source) {
      result = result.filter((a) => a.source === source)
    }

    // Within a category, any of the chosen tags will do; across categories, all
    // of them must hold. Picking two products means either product; adding a
    // packaging format narrows that to the ones that are also bottles.
    for (const group of tagGroups ?? []) {
      if (group.length === 0) continue
      result = result.filter((a) => group.some((tagId) => a.tagIds.includes(tagId)))
    }

    if (packaging !== 'all') {
      const nonPackaging = this.tags.find((t) => (t.slug ?? t.id) === NON_PACKAGING_SLUG)
      if (nonPackaging) {
        const carries = (a: Asset) => a.tagIds.includes(nonPackaging.id)
        result = result.filter((a) => (packaging === 'non-packaging' ? carries(a) : !carries(a)))
      } else if (packaging === 'non-packaging') {
        // Nothing has ever been filed as non-packaging, so asking for only
        // those is asking for nothing — not for everything.
        result = []
      }
    }

    const trimmed = text?.trim()
    if (trimmed) {
      result = result.filter((a) => matchesText(a, trimmed, this.tagNameById))
    }

    // Same order the database uses, and for the same reason: created_at alone
    // is not unique, so without the id tiebreak a cursor could skip or repeat a
    // row that shares its timestamp with another.
    const ordered = [...result].sort((a, b) =>
      a.createdAt === b.createdAt
        ? b.id.localeCompare(a.id)
        : b.createdAt.localeCompare(a.createdAt),
    )

    const total = ordered.length
    const startAt = after
      ? ordered.findIndex((a) => a.createdAt === after.createdAt && a.id === after.id) + 1
      : 0
    const items = ordered.slice(startAt, startAt + limit)
    const last = items[items.length - 1]

    return delay({
      items,
      total,
      next: last && startAt + items.length < total ? { createdAt: last.createdAt, id: last.id } : null,
    })
  }

  async getById(id: string): Promise<Asset | null> {
    return delay(this.assets.find((a) => a.id === id) ?? null, 40)
  }

  async getByIds(ids: string[]): Promise<Asset[]> {
    const set = new Set(ids)
    return delay(
      this.assets.filter((a) => set.has(a.id)),
      40,
    )
  }

  async listTagGroups(): Promise<TagGroupDef[]> {
    return delay(MOCK_TAG_GROUPS, 40)
  }

  async listTags(): Promise<Tag[]> {
    // Count assets per tag so the UI can show numbers next to each chip
    const counts = new Map<string, number>()
    for (const asset of this.assets) {
      for (const tagId of asset.tagIds) {
        counts.set(tagId, (counts.get(tagId) ?? 0) + 1)
      }
    }
    const withCounts = this.tags.map((t) => ({ ...t, assetCount: counts.get(t.id) ?? 0 }))
    return delay(withCounts, 40)
  }
}

let repository: AssetRepository | null = null

/**
 * Resolve the active data source. This is the one place that knows which
 * backing store is live; nothing above it does.
 *
 * VITE_DATA_SOURCE=mock     (default) -> images from library/, no account needed
 * VITE_DATA_SOURCE=supabase           -> the real database
 *
 * Asking for Supabase without the URL and anon key falls back to mock data with
 * a warning rather than rendering an empty library, so a checkout with no
 * .env.local still runs.
 */
export function getAssetRepository(): AssetRepository {
  if (repository) return repository

  const source = import.meta.env.VITE_DATA_SOURCE ?? 'mock'

  if (source === 'supabase') {
    if (isSupabaseConfigured()) {
      repository = new SupabaseAssetRepository()
      return repository
    }
    console.warn(
      '[WonderWorks] VITE_DATA_SOURCE=supabase, but VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are missing. Falling back to mock data — copy .env.example to .env.local.',
    )
  }

  repository = new MockAssetRepository()
  return repository
}
